import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ServiceManager from './ServiceManager';
import PackManager from './PackManager';
import PromotionManager from './PromotionManager';
import WheelManager from './WheelManager';
import AppointmentManager from './AppointmentManager';
import DiscussionManager from './DiscussionManager';
import ServicesPageConfig from './ServicesPageConfig';
import { serviceApiService, Service } from '../services/serviceApi';

interface DashboardAdminProps {
  onClose: () => void;
}

const DashboardAdmin: React.FC<DashboardAdminProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<'services' | 'packs' | 'promotions' | 'wheel' | 'appointments' | 'discussions' | 'servicespage'>('services');
  const [services, setServices] = useState<Service[]>([]);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const servicesData = await serviceApiService.getServices();
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (err) {
      console.error('Erreur chargement services:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="h-full bg-white"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">⚙️</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard Admin</h1>
                <p className="text-gray-600">Gestion des services et promotions</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button onClick={() => setActiveTab('services')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'services' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-2">
                  <span>📋</span>
                  <span>Services</span>
                </div>
              </button>
              
              <button onClick={() => setActiveTab('packs')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'packs' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-2">
                  <span>📦</span>
                  <span>Packs</span>
                </div>
              </button>
              
              <button onClick={() => setActiveTab('promotions')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'promotions' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-2">
                  <span>🎯</span>
                  <span>Promotions</span>
                </div>
              </button>
              
              <button onClick={() => setActiveTab('wheel')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'wheel' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-2">
                  <span>🎰</span>
                  <span>Roue de Promo</span>
                </div>
              </button>
              
              <button onClick={() => setActiveTab('appointments')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'appointments' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-2">
                  <span>📅</span>
                  <span>Rendez-vous</span>
                </div>
              </button>
              
              <button onClick={() => setActiveTab('discussions')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'discussions' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-2">
                  <span>💬</span>
                  <span>Discussions</span>
                </div>
              </button>
              
              <button onClick={() => setActiveTab('servicespage')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'servicespage' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-2">
                  <span>🎨</span>
                  <span>Config ServicesPage</span>
                </div>
              </button>
            </nav>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
            {activeTab === 'services' && <ServiceManager />}
            {activeTab === 'packs' && (
              <div className="p-6">
                <PackManager services={services} />
              </div>
            )}
            {activeTab === 'promotions' && (
              <div className="p-6">
                <PromotionManager onClose={() => {}} />
              </div>
            )}
            {activeTab === 'wheel' && <WheelManager />}
            {activeTab === 'appointments' && <AppointmentManager />}
            {activeTab === 'discussions' && <DiscussionManager />}
            {activeTab === 'servicespage' && <ServicesPageConfig />}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardAdmin;
